/**
 * AgentRosterList — bridge wave-2 item 5: the left rail's scrolling agent
 * roster. The constellation's army-in-formation, rendered as dense 24px
 * single-line DM Mono rows grouped by stage (PLAN…DEEP) plus the run-level
 * BELT group.
 *
 * SINGLE-SOURCE RULE (FM1): every name rendered here comes from
 * STAGE_AGENTS (fleet/AssemblyLine.tsx — its one home) and BELT_AGENTS
 * (bridge/agentOrbit.ts). No roster is ever copied into this file; an orbit
 * change flows straight through.
 *
 * STATE DERIVATION (FM5): the dot's lit/dim state comes from agentOrbit's
 * filterRunSessions + agentSessionState — the EXACT functions
 * AgentConstellation uses — so the rail rows and the core nodes can never
 * show conflicting live state:
 *   open      → full #FF6600 (ACCENT) + pulse at the mood period // design-law-allow
 *   completed → ACCENT at 70%
 *   none      → white at 14%
 *
 * MOTION: the only animation is the open-dot pulse, bound to
 * MOOD_STYLE[mood].pulseSeconds (a FleetMood condition — never unbound).
 * It is JS-stripped under mood 'down' and prefers-reduced-motion, AND
 * CSS-stripped by the media query below; with motion off the dot remains
 * fully lit, so no state is ever carried by motion alone.
 *
 * Chrome: squared (no rounding on any chrome — the state dot is a dot,
 * rounded-full by dot convention, never a rounded panel), hairline group
 * dividers, house tokens only.
 */
import React, { useMemo } from 'react';
import { FleetSession } from '../../services/fleetHudApi';
import { FleetMood, MOOD_STYLE } from '../fleet/fleetMood';
import { ACCENT } from '../fleet/ReactorCore';
import { ASSEMBLY_STAGES, STAGE_AGENTS } from '../fleet/AssemblyLine';
import { usePrefersReducedMotion } from './BridgeCore';
import { BELT_AGENTS, agentSessionState, filterRunSessions } from './agentOrbit';

interface AgentRosterListProps {
  /** Active run's sessions — the SAME prop AgentConstellation receives.
      undefined = unavailable → every dot degrades to 'none'. */
  sessions?: FleetSession[];
  /** Active run id; sessions of other runs never light a dot. */
  activeRunId?: string | null;
  /** Fleet mood — binds the open-dot pulse period (MOOD_STYLE). */
  mood: FleetMood;
  className?: string;
}

/** Component-scoped CSS, arl- prefixed. Opacity-only keyframes; the reduced-
    motion media query is the CSS half of the collapse (JS strip is below). */
const ARL_CSS = `
.arl-dot--open { animation-name: arl-pulse; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
@keyframes arl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
@media (prefers-reduced-motion: reduce) {
  .arl-root, .arl-root * { animation: none !important; }
}
`;

function GroupHeader({ label }: { label: string }) {
  return (
    <div className="flex h-5 items-center border-t border-white/[0.06] px-3 font-mono text-[9px] uppercase tracking-[0.25em] text-text-muted first:border-t-0">
      {label}
    </div>
  );
}

export default function AgentRosterList({
  sessions,
  activeRunId = null,
  mood,
  className,
}: AgentRosterListProps) {
  const reducedMotion = usePrefersReducedMotion();
  // Down mood = zero animation anywhere in the bridge tree (BridgeCore
  // contract); reduced motion strips the class the same way.
  const motionOff = mood === 'down' || reducedMotion;
  const pulseSeconds = MOOD_STYLE[mood].pulseSeconds;

  // THE shared selector — identical to AgentConstellation's (agentOrbit).
  const runSessions = useMemo(
    () => filterRunSessions(sessions, activeRunId),
    [sessions, activeRunId]
  );

  const renderRow = (agentType: string) => {
    const state = agentSessionState(runSessions, agentType);
    const animated = state === 'open' && !motionOff && pulseSeconds > 0;
    // House tokens only: 'none' is white-at-14% via the Tailwind alpha
    // utility; lit states use the ACCENT token constant (#FF6600). // design-law-allow
    const dotClass = [
      'h-1.5 w-1.5 shrink-0 rounded-full',
      state === 'none' ? 'bg-white/[0.14]' : '',
      animated ? 'arl-dot--open' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const dotStyle: React.CSSProperties =
      state === 'none'
        ? {}
        : {
            backgroundColor: ACCENT,
            opacity: state === 'completed' ? 0.7 : 1,
            ...(animated ? { animationDuration: `${pulseSeconds}s` } : {}),
          };
    return (
      <div
        key={agentType}
        data-agent-type={agentType}
        className="flex h-6 items-center gap-2 px-3 font-mono text-[11px] leading-none"
        title={agentType}
      >
        <span
          data-state={state}
          className={dotClass}
          style={dotStyle}
        />
        <span
          className={`truncate ${
            state === 'open'
              ? 'text-text-primary'
              : state === 'completed'
                ? 'text-text-secondary'
                : 'text-text-muted'
          }`}
        >
          {agentType}
        </span>
      </div>
    );
  };

  return (
    <div
      data-testid="agent-roster-list"
      className={`arl-root min-h-0 flex-1 overflow-y-auto py-1${className ? ` ${className}` : ''}`}
    >
      <style>{ARL_CSS}</style>
      {ASSEMBLY_STAGES.map((stage) => (
        <div key={stage} data-stage-group={stage}>
          <GroupHeader label={stage} />
          {(STAGE_AGENTS[stage] ?? []).map(renderRow)}
        </div>
      ))}
      <div data-stage-group="BELT">
        <GroupHeader label="BELT" />
        {BELT_AGENTS.map(renderRow)}
      </div>
    </div>
  );
}
